import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip, Dropdown, Input, Popover, Modal } from 'antd';
import OutlookSignatureManager from './OutlookSignatureManager';
import {
  PaperClipOutlined,
  LinkOutlined,
  PictureOutlined,
  TableOutlined,
  SmileOutlined,
  SignatureOutlined,
  SaveOutlined,
  CloseOutlined,
  DownOutlined,
  CheckOutlined,
  EyeOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  LockOutlined,
  UserAddOutlined,
  MailOutlined,
  CheckCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAlignLeft, faAlignCenter, faAlignRight, faAlignJustify,
  faListUl, faListOl,
  faIndent, faOutdent,
  faFont,
  faPaperPlane,
  faLink,
} from '@fortawesome/free-solid-svg-icons';

import {
  selectRibbonState,
  setRibbonTab,
  clearInlineCompose,
} from '../../../Slices/mailUiSlice';
import {
  registry, execOnCompose,
  applyStyle, applyFontSize, applyFontFamily,
  applyBold, applyItalic, applyUnderline, applyStrikethrough,
  saveSelection, restoreSelection,
  insertSignatureHtml,
  undoHistory, redoHistory,
} from './composeRegistry';

// ---------------------------------------------------------------------------
// Design tokens — mirrors MailRibbon.jsx
// ---------------------------------------------------------------------------

const C = {
  blue:       '#0f6cbd',
  blueDark:   '#115ea3',
  blueLight:  '#eff6fc',
  blueBg:     '#deecf9',
  text:       '#242424',
  textSec:    '#605e5c',
  textMuted:  '#8a8886',
  border:     '#edebe9',
  bg:         '#fff',
  bgAlt:      '#faf9f8',
  bgHover:    '#f3f2f1',
  red:        '#c50f1f',
  redBg:      '#fde7e9',
  orange:     '#ca5010',
  green:      '#107c10',
  greenBg:    '#dff6dd',
  purple:     '#7719aa',
  activeTab:  '#0f6cbd',
};
const FONT = "'Segoe UI', Tahoma, sans-serif";

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------

const TABS = [
  { id: 'compose.message',    label: 'Message'     },
  { id: 'compose.insert',     label: 'Insert'      },
  { id: 'compose.formattext', label: 'Format Text' },
  { id: 'compose.options',    label: 'Options'     },
];

// ---------------------------------------------------------------------------
// Primitive sub-components
// ---------------------------------------------------------------------------

/** Large ribbon button (icon stacked above label) */
function RibbonButton({ icon, label, onClick, onMouseDown, disabled = false,
  active = false, danger = false, title }) {
  return (
    <Tooltip title={title || label} mouseEnterDelay={0.8} placement="bottom">
      <button
        className={[
          'cr-btn',
          active   ? 'cr-btn--active'   : '',
          danger   ? 'cr-btn--danger'   : '',
          disabled ? 'cr-btn--disabled' : '',
        ].filter(Boolean).join(' ')}
        onClick={!disabled ? onClick : undefined}
        onMouseDown={!disabled ? onMouseDown : undefined}
        disabled={disabled}
        style={s.btn}
      >
        <span style={s.btnIcon}>{icon}</span>
        {label && <span style={s.btnLabel}>{label}</span>}
      </button>
    </Tooltip>
  );
}



/** Toggle small button — renders a checkbox-style indicator */
function RibbonToggle({ icon, label, checked = false, onChange, disabled = false, title }) {
  return (
    <Tooltip title={title || label} mouseEnterDelay={0.8} placement="bottom">
      <button
        className={[
          'cr-sbtn',
          checked  ? 'cr-sbtn--active'   : '',
          disabled ? 'cr-sbtn--disabled' : '',
        ].filter(Boolean).join(' ')}
        onClick={!disabled ? onChange : undefined}
        disabled={disabled}
        style={s.sbtn}
        role="checkbox"
        aria-checked={checked}
      >
        <span style={{ fontSize: 13, display: 'flex', alignItems: 'center' }}>{icon}</span>
        <span style={s.sbtnLabel}>{label}</span>
        {checked && (
          <CheckOutlined style={{ fontSize: 10, color: C.blue, marginLeft: 3 }} />
        )}
      </button>
    </Tooltip>
  );
}

/** Vertical separator */
const Sep = () => <div style={s.sep} />;

/** Group with optional bottom label */
function RibbonGroup({ label, children, column = false }) {
  return (
    <div style={s.group}>
      <div style={{ ...s.groupContent, flexDirection: column ? 'column' : 'row' }}>
        {children}
      </div>
      {label && <span style={s.groupLabel}>{label}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Message tab
// ---------------------------------------------------------------------------

function MessageTab({ onSend, onSaveDraft, onDiscard, onAttach }) {
  const dispatch = useDispatch();

  // Helpers that route through the registry (compose overlay) first,
  // then fall back to the prop callbacks for legacy/inline usage.
  const doSend      = () => registry.submitForm    ? registry.submitForm()    : onSend?.();
  // doDiscard uses handleDiscard (deletes draft) not handleClose (saves draft)
  const doDiscard   = () => registry.handleDiscard ? registry.handleDiscard() : (onDiscard ? onDiscard() : dispatch(clearInlineCompose()));
  const doSaveDraft = () => registry.saveDraft     ? registry.saveDraft()     : onSaveDraft?.();
  const doAttach    = () => registry.triggerAttach ? registry.triggerAttach() : onAttach?.();

  return (
    <div style={s.tabContent}>

      {/* ── Send / Save ── */}
      <RibbonGroup label="Send">
        <RibbonButton
          icon={<FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: 18, color: C.blue }} />}
          label="Send"
          title="Send (Ctrl+Enter)"
          onClick={doSend}
        />
        <RibbonButton
          icon={<SaveOutlined style={{ fontSize: 19, color: C.textSec }} />}
          label="Save Draft"
          title="Save as draft"
          onClick={doSaveDraft}
        />
        <RibbonButton
          icon={<CloseOutlined style={{ fontSize: 18, color: C.red }} />}
          label="Discard"
          title="Discard and close"
          danger
          onClick={doDiscard}
        />
      </RibbonGroup>
      <Sep />

      {/* ── Basic Text ── */}
      <BasicTextGroup />
      <Sep />

      {/* ── Paragraph ── */}
      <ParagraphGroup />
      <Sep />

      {/* ── Include ── */}
      <RibbonGroup label="Include">
        <RibbonButton
          icon={<PaperClipOutlined style={{ fontSize: 20, color: C.textSec }} />}
          label="Attach File"
          title="Attach a file"
          onClick={doAttach}
        />
        <RibbonButton
          icon={<LinkOutlined style={{ fontSize: 19, color: C.textSec }} />}
          label="Link"
          title="Insert hyperlink (Ctrl+K)"
          onMouseDown={(e) => {
            e.preventDefault();
            const url = window.prompt('Enter URL:');
            if (url) execOnCompose('createLink', url);
          }}
        />
      </RibbonGroup>

    </div>
  );
}

// ---------------------------------------------------------------------------
// Insert tab
// ---------------------------------------------------------------------------

function InsertTab() {
  const signatures  = useSelector(s => s?.sales?.signatures || []);
  const [linkOpen,   setLinkOpen]   = useState(false);
  const [linkUrl,    setLinkUrl]    = useState('');
  const [sigMgrOpen, setSigMgrOpen] = useState(false);
  const [tableOpen,  setTableOpen]  = useState(false);
  const [tableHover, setTableHover] = useState({ r: 0, c: 0 });
  const [emojiOpen,  setEmojiOpen]  = useState(false);
  const picInputRef = useRef(null);

  const insertSignature = (sig) => {
    insertSignatureHtml(
      `<div class="bpa-signature" style="border-top:1px solid #edebe9;margin-top:12px;padding-top:10px;">${sig.content}</div>`
    );
  };

  const handleInsertLink = () => {
    const url = linkUrl.trim();
    if (!url) return;
    execOnCompose('createLink', url.startsWith('http') ? url : `https://${url}`);
    setLinkUrl('');
    setLinkOpen(false);
  };

  const linkPopoverContent = (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '2px 0' }}>
      <Input
        value={linkUrl}
        onChange={e => setLinkUrl(e.target.value)}
        onPressEnter={handleInsertLink}
        placeholder="https://example.com"
        style={{ width: 260, fontSize: 13 }}
        autoFocus
      />
      <button
        onClick={handleInsertLink}
        style={{
          height: 32, padding: '0 14px', background: C.blue, color: '#fff',
          border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13,
          fontFamily: FONT, fontWeight: 600,
        }}
      >
        Insert
      </button>
    </div>
  );

  const TABLE_MAX = 8;

  const insertTable = (rows, cols) => {
    let html = '<table style="border-collapse:collapse;width:auto;margin:4px 0">';
    for (let r = 0; r < rows; r++) {
      html += '<tr>';
      for (let c = 0; c < cols; c++) {
        html += '<td style="border:1px solid #d0d0d0;padding:6px 12px;min-width:60px;height:24px">&nbsp;</td>';
      }
      html += '</tr>';
    }
    html += '</table><p><br></p>';
    execOnCompose('insertHTML', html);
    setTableOpen(false);
  };

  const handlePicSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      execOnCompose('insertHTML', `<img src="${ev.target.result}" style="max-width:100%;height:auto;display:block" />`);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const EMOJIS = [
    '😀','😁','😂','🤣','😄','😅','😊','😎','😍','🥰','😘','🙂',
    '😐','🙄','😏','😮','🤐','😴','😌','😛','🤔','🤗','😬','😱',
    '👍','👎','👌','✌️','🤞','👋','🙏','💪','🎉','🎊','🎁','🏆',
    '❤️','🧡','💛','💚','💙','💜','🖤','💔','💯','✅','❌','⭐',
    '🔥','💡','📎','📧','📅','💬','🔔','⚠️','ℹ️','✏️','📝','🔗',
  ];

  const tablePickerContent = (
    <div style={{ padding: '4px 0' }}>
      <div style={{ fontSize: 11, color: C.textMuted, fontFamily: FONT, marginBottom: 6, textAlign: 'center' }}>
        {tableHover.r > 0 ? `${tableHover.r} × ${tableHover.c} Table` : 'Insert Table'}
      </div>
      <div
        style={{ display: 'grid', gridTemplateColumns: `repeat(${TABLE_MAX}, 20px)`, gap: 2 }}
        onMouseLeave={() => setTableHover({ r: 0, c: 0 })}
      >
        {Array.from({ length: TABLE_MAX * TABLE_MAX }).map((_, i) => {
          const row = Math.floor(i / TABLE_MAX) + 1;
          const col = (i % TABLE_MAX) + 1;
          const active = row <= tableHover.r && col <= tableHover.c;
          return (
            <div
              key={i}
              onMouseEnter={() => setTableHover({ r: row, c: col })}
              onMouseDown={(e) => { e.preventDefault(); insertTable(row, col); }}
              style={{
                width: 20, height: 20,
                border: `1px solid ${active ? C.blue : '#ccc'}`,
                background: active ? C.blueLight : '#fff',
                cursor: 'pointer', boxSizing: 'border-box',
              }}
            />
          );
        })}
      </div>
    </div>
  );

  const emojiPickerContent = (
    <div style={{ width: 228 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2 }}>
        {EMOJIS.map(emoji => (
          <button
            key={emoji}
            onMouseDown={(e) => {
              e.preventDefault();
              execOnCompose('insertHTML', emoji);
              setEmojiOpen(false);
            }}
            style={{
              border: 'none', background: 'transparent', cursor: 'pointer',
              fontSize: 16, padding: 2, borderRadius: 3,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            className="cr-fmtbtn"
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div style={s.tabContent}>
      <input ref={picInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePicSelect} />

      {/* ── Tables ── */}
      <RibbonGroup label="Tables">
        <Popover
          content={tablePickerContent}
          title={null}
          trigger="click"
          open={tableOpen}
          onOpenChange={(v) => { setTableOpen(v); if (!v) setTableHover({ r: 0, c: 0 }); }}
          placement="bottomLeft"
        >
          <div>
            <RibbonButton
              icon={<TableOutlined style={{ fontSize: 20, color: C.textSec }} />}
              label="Table"
              title="Insert table"
              active={tableOpen}
            />
          </div>
        </Popover>
      </RibbonGroup>
      <Sep />

      {/* ── Illustrations ── */}
      <RibbonGroup label="Illustrations">
        <RibbonButton
          icon={<PictureOutlined style={{ fontSize: 20, color: C.textSec }} />}
          label="Pictures"
          title="Insert picture from file"
          onClick={() => picInputRef.current?.click()}
        />
      </RibbonGroup>
      <Sep />

      {/* ── Links ── */}
      <RibbonGroup label="Links">
        <Popover
          content={linkPopoverContent}
          title="Insert hyperlink"
          trigger="click"
          open={linkOpen}
          onOpenChange={(v) => { setLinkOpen(v); if (!v) setLinkUrl(''); }}
          placement="bottomLeft"
        >
          <div>
            <RibbonButton
              icon={<FontAwesomeIcon icon={faLink} style={{ fontSize: 18, color: C.blue }} />}
              label="Hyperlink"
              title="Insert hyperlink (Ctrl+K)"
              active={linkOpen}
            />
          </div>
        </Popover>
      </RibbonGroup>
      <Sep />

      {/* ── Text ── */}
      <RibbonGroup label="Text">
        <Dropdown
          trigger={['click']}
          placement="bottomLeft"
          menu={{
            items: [
              ...signatures.map(sig => ({
                key: sig.id,
                label: sig.name,
                onClick: () => insertSignature(sig),
              })),
              ...(signatures.length ? [{ type: 'divider' }] : []),
              { key: '__manage', label: 'Manage Signatures…', onClick: () => setSigMgrOpen(true) },
            ],
          }}
        >
          <div>
            <RibbonButton
              icon={<SignatureOutlined style={{ fontSize: 19, color: C.textSec }} />}
              label="Signature"
              title="Insert or manage signatures"
            />
          </div>
        </Dropdown>
        <Popover
          content={emojiPickerContent}
          title={null}
          trigger="click"
          open={emojiOpen}
          onOpenChange={setEmojiOpen}
          placement="bottomLeft"
        >
          <div>
            <RibbonButton
              icon={<SmileOutlined style={{ fontSize: 19, color: C.textSec }} />}
              label="Emoji"
              title="Insert emoji"
              active={emojiOpen}
            />
          </div>
        </Popover>
      </RibbonGroup>
      <Sep />

      {/* ── Attach ── */}
      <RibbonGroup label="Include">
        <RibbonButton
          icon={<PaperClipOutlined style={{ fontSize: 20, color: C.textSec }} />}
          label="Attach File"
          title="Attach a file"
          onClick={() => registry.triggerAttach?.()}
        />
      </RibbonGroup>

      {/* ── Signature manager modal ── */}
      <Modal
        open={sigMgrOpen}
        onCancel={() => setSigMgrOpen(false)}
        footer={null}
        width={680}
        styles={{ body: { padding: 0, height: 560, overflow: 'hidden' } }}
        title={null}
        destroyOnClose
      >
        <OutlookSignatureManager onClose={() => setSigMgrOpen(false)} />
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Color constants
// ---------------------------------------------------------------------------

const TEXT_COLORS = [
  '#000000','#434343','#666666','#999999','#b7b7b7','#cccccc','#d9d9d9','#ffffff',
  '#ff0000','#ff4500','#ff8c00','#ffd700','#00cc00','#00ced1','#1e90ff','#8a2be2',
  '#c50f1f','#ca5010','#986f0b','#107c10','#006ebe','#00377a','#7b00d4','#5c005c',
  '#ffd2d2','#ffe8cc','#fff3cc','#e2f0d9','#dce6f1','#cfe2f3','#e8d5f5','#f0e0f0',
];

const HIGHLIGHT_COLORS = [
  '#ffff00','#00ff00','#00ffff','#ff00ff',
  '#ff4500','#0000ff','#ff69b4','#c0c0c0',
  '#808080','transparent',
];

const FONT_NAMES = [
  'Aptos','Calibri','Arial','Helvetica','Times New Roman',
  'Georgia','Courier New','Verdana','Tahoma','Trebuchet MS',
];

const FONT_SIZES = [8,9,10,11,12,14,16,18,20,22,24,28,36,48,72];

// ---------------------------------------------------------------------------
// useActiveStates — tracks queryCommandState for the focused editor
// ---------------------------------------------------------------------------

function useActiveStates() {
  const [active, setActive]       = useState({});
  const [fontName, setFontName]   = useState('Calibri');
  const [fontSize, setFontSize]   = useState('12');

  useEffect(() => {
    function update() {
      try {
        const sel = window.getSelection();
        const anchorNode = sel?.anchorNode;
        const el = anchorNode
          ? (anchorNode.nodeType === Node.TEXT_NODE ? anchorNode.parentElement : anchorNode)
          : null;
        const cs = el ? window.getComputedStyle(el) : null;
        const td = cs?.textDecoration ?? '';

        setActive({
          // Computed-style detection — works with our span-based model
          bold:                cs ? parseInt(cs.fontWeight, 10) >= 700 : false,
          italic:              cs?.fontStyle === 'italic',
          underline:           td.includes('underline'),
          strikeThrough:       td.includes('line-through'),
          // execCommand state still reliable for these (block / structural)
          subscript:           document.queryCommandState('subscript'),
          superscript:         document.queryCommandState('superscript'),
          justifyLeft:         document.queryCommandState('justifyLeft'),
          justifyCenter:       document.queryCommandState('justifyCenter'),
          justifyRight:        document.queryCommandState('justifyRight'),
          justifyFull:         document.queryCommandState('justifyFull'),
          insertUnorderedList: document.queryCommandState('insertUnorderedList'),
          insertOrderedList:   document.queryCommandState('insertOrderedList'),
        });

        if (cs) {
          const ff = cs.fontFamily.replace(/['"]/g, '').split(',')[0].trim();
          if (ff) setFontName(ff);
          const px = parseFloat(cs.fontSize);
          if (!isNaN(px)) setFontSize(String(Math.round(px / 1.3333)));
        }
      } catch (_) {}
    }
    document.addEventListener('selectionchange', update);
    return () => document.removeEventListener('selectionchange', update);
  }, []);

  return { active, fontName, fontSize };
}

// ---------------------------------------------------------------------------
// ColorGrid — swatch picker used inside Popover
// ---------------------------------------------------------------------------

function ColorGrid({ colors, onSelect, cols = 8 }) {
  return (
    <div style={{ display:'grid', gridTemplateColumns:`repeat(${cols}, 18px)`, gap:3, padding:4 }}>
      {colors.map(color => (
        <Tooltip key={color} title={color === 'transparent' ? 'No color' : color} mouseEnterDelay={1}>
          <div
            onMouseDown={(e) => { e.preventDefault(); onSelect(color); }}
            style={{
              width:18, height:18, borderRadius:2, cursor:'pointer', boxSizing:'border-box',
              background: color === 'transparent'
                ? 'linear-gradient(135deg,#fff 40%,#f00 40%,#f00 60%,#fff 60%)'
                : color,
              border: '1px solid rgba(0,0,0,0.2)',
            }}
          />
        </Tooltip>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// InlineSelect — compact font-name / font-size dropdown trigger
// ---------------------------------------------------------------------------

function InlineSelect({ value, width, items, onSave }) {
  return (
    <Dropdown
      trigger={['click']}
      placement="bottomLeft"
      menu={{ items }}
    >
      <div
        onMouseDown={onSave}
        style={{
          display:'flex', alignItems:'center', gap:3,
          width, height:24, padding:'0 6px',
          border:`1px solid ${C.border}`, borderRadius:3,
          background:'#fff', cursor:'pointer',
          fontFamily:FONT, fontSize:12, color:C.text,
          userSelect:'none',
        }}
      >
        <span style={{ flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {value}
        </span>
        <DownOutlined style={{ fontSize:8, color:C.textSec, flexShrink:0 }} />
      </div>
    </Dropdown>
  );
}

// ---------------------------------------------------------------------------
// FmtBtn — compact formatting button with active state
// ---------------------------------------------------------------------------

function FmtBtn({ children, title, onMouseDown, active = false }) {
  return (
    <Tooltip title={title} mouseEnterDelay={0.8} placement="bottom">
      <button
        onMouseDown={onMouseDown}
        style={{
          display:'inline-flex', alignItems:'center', justifyContent:'center',
          minWidth:24, height:24, padding:'0 3px',
          background: active ? C.blueBg : 'transparent',
          border: active ? `1px solid ${C.blue}` : '1px solid transparent',
          borderRadius:3, cursor:'pointer', color: active ? C.blue : C.text,
          fontFamily:FONT, transition:'background .1s',
        }}
        className="cr-fmtbtn"
      >
        {children}
      </button>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// SplitColorBtn — left=apply last color, right arrow=open picker
// ---------------------------------------------------------------------------

function SplitColorBtn({ color, onApply, colors, onPickColor, icon, title, pickerTitle }) {
  const [open, setOpen] = useState(false);
  const content = (
    <div>
      <div style={{ fontSize:11, color:C.textMuted, fontFamily:FONT, marginBottom:5, fontWeight:600 }}>
        {pickerTitle}
      </div>
      <ColorGrid colors={colors} onSelect={(c) => { onPickColor(c); setOpen(false); }} />
    </div>
  );
  return (
    <div style={{ display:'flex', alignItems:'stretch' }}>
      <Tooltip title={title} mouseEnterDelay={0.8}>
        <button
          onMouseDown={onApply}
          style={{
            display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
            width:24, height:24, padding:'2px 3px', gap:1,
            background:'transparent', border:'1px solid transparent', borderRadius:'3px 0 0 3px',
            cursor:'pointer', color:C.text,
          }}
          className="cr-fmtbtn"
        >
          {icon}
          <div style={{
            width:14, height:3, borderRadius:1,
            background: color === 'transparent' ? 'none' : color,
            border: color === 'transparent' ? '1px dashed #999' : 'none',
          }} />
        </button>
      </Tooltip>
      <Popover content={content} title={null} trigger="click" open={open} onOpenChange={setOpen} placement="bottomLeft">
        <button
          onMouseDown={saveSelection}
          style={{
            display:'flex', alignItems:'center', justifyContent:'center',
            width:12, height:24, padding:0,
            background:'transparent', border:'1px solid transparent', borderLeft:'none',
            borderRadius:'0 3px 3px 0', cursor:'pointer', color:C.textSec,
          }}
          className="cr-fmtbtn"
        >
          <DownOutlined style={{ fontSize:7 }} />
        </button>
      </Popover>
    </div>
  );
}

// ---------------------------------------------------------------------------
// BasicTextGroup — shared between MessageTab and FormatTextTab
// ---------------------------------------------------------------------------

function BasicTextGroup() {
  const { active, fontName, fontSize } = useActiveStates();
  const [hlColor,   setHlColor]   = useState('#ffff00');
  const [txtColor,  setTxtColor]  = useState('#000000');

  const fmt = (cmd, val = null) => (e) => {
    e.preventDefault();
    execOnCompose(cmd, val);
  };

  const applyHL = (e) => { e.preventDefault(); execOnCompose('hiliteColor', hlColor); };
  const applyTC = (e) => { e.preventDefault(); applyStyle({ color: txtColor }); };

  const fontItems = FONT_NAMES.map(f => ({
    key: f,
    label: <span style={{ fontFamily:f, fontSize:13 }}>{f}</span>,
    onClick: () => applyFontFamily(f),
  }));

  const sizeItems = FONT_SIZES.map(sz => ({
    key: String(sz),
    label: <span style={{ fontSize:13 }}>{sz}</span>,
    onClick: () => applyFontSize(sz),
  }));

  return (
    <RibbonGroup label="Basic Text">
      {/* Two-row compact layout matching Outlook */}
      <div style={{ display:'flex', flexDirection:'column', gap:3 }}>

        {/* Row 1: Font name | Size | Sub | Super */}
        <div style={{ display:'flex', alignItems:'center', gap:3 }}>
          <InlineSelect value={fontName} width={108} items={fontItems} onSave={saveSelection} />
          <InlineSelect value={fontSize} width={44}  items={sizeItems} onSave={saveSelection} />
          <FmtBtn title="Subscript"   active={active.subscript}   onMouseDown={fmt('subscript')}>
            <span style={{ fontSize:11 }}>X<sub style={{ fontSize:8 }}>2</sub></span>
          </FmtBtn>
          <FmtBtn title="Superscript" active={active.superscript} onMouseDown={fmt('superscript')}>
            <span style={{ fontSize:11 }}>X<sup style={{ fontSize:8 }}>2</sup></span>
          </FmtBtn>
          <FmtBtn title="Clear Formatting" onMouseDown={fmt('removeFormat')}>
            <span style={{ fontSize:12, fontFamily:FONT, position:'relative' }}>
              T<span style={{ fontSize:8, position:'absolute', bottom:-1, right:-3, color:C.textMuted }}>✕</span>
            </span>
          </FmtBtn>
        </div>

        {/* Row 2: B I U S | Highlight | Text color */}
        <div style={{ display:'flex', alignItems:'center', gap:2 }}>
          <FmtBtn title="Bold (Ctrl+B)"      active={active.bold}         onMouseDown={(e) => { e.preventDefault(); applyBold(); }}>
            <span style={{ fontWeight:800, fontSize:13, fontFamily:'Georgia,serif' }}>B</span>
          </FmtBtn>
          <FmtBtn title="Italic (Ctrl+I)"    active={active.italic}       onMouseDown={(e) => { e.preventDefault(); applyItalic(); }}>
            <span style={{ fontStyle:'italic', fontSize:13, fontFamily:'Georgia,serif' }}>I</span>
          </FmtBtn>
          <FmtBtn title="Underline (Ctrl+U)" active={active.underline}    onMouseDown={(e) => { e.preventDefault(); applyUnderline(); }}>
            <span style={{ textDecoration:'underline', fontSize:13, fontFamily:'Georgia,serif' }}>U</span>
          </FmtBtn>
          <FmtBtn title="Strikethrough"      active={active.strikeThrough} onMouseDown={(e) => { e.preventDefault(); applyStrikethrough(); }}>
            <span style={{ textDecoration:'line-through', fontSize:13, fontFamily:'Georgia,serif' }}>S</span>
          </FmtBtn>
          <div style={{ width:1, height:16, background:C.border, margin:'0 2px' }} />
          <SplitColorBtn
            color={hlColor}
            onApply={applyHL}
            colors={HIGHLIGHT_COLORS}
            onPickColor={(c) => { setHlColor(c); restoreSelection(); execOnCompose('hiliteColor', c); }}
            icon={<span style={{ fontSize:11 }}>ab</span>}
            title="Highlight Text"
            pickerTitle="Highlight Color"
          />
          <SplitColorBtn
            color={txtColor}
            onApply={applyTC}
            colors={TEXT_COLORS}
            onPickColor={(c) => { setTxtColor(c); applyStyle({ color: c }); }}
            icon={<span style={{ fontWeight:700, fontSize:12, fontFamily:'Georgia,serif' }}>A</span>}
            title="Font Color"
            pickerTitle="Font Color"
          />
        </div>
      </div>
    </RibbonGroup>
  );
}

// ---------------------------------------------------------------------------
// ParagraphGroup — shared between MessageTab and FormatTextTab
// ---------------------------------------------------------------------------

function ParagraphGroup() {
  const { active } = useActiveStates();
  const fmt = (cmd) => (e) => { e.preventDefault(); execOnCompose(cmd); };

  const currentAlign =
    active.justifyCenter ? 'center'  :
    active.justifyRight  ? 'right'   :
    active.justifyFull   ? 'justify' : 'left';

  const alignIcon = {
    left:    <FontAwesomeIcon icon={faAlignLeft}    style={{ fontSize:12 }} />,
    center:  <FontAwesomeIcon icon={faAlignCenter}  style={{ fontSize:12 }} />,
    right:   <FontAwesomeIcon icon={faAlignRight}   style={{ fontSize:12 }} />,
    justify: <FontAwesomeIcon icon={faAlignJustify} style={{ fontSize:12 }} />,
  }[currentAlign];

  const alignItems = [
    { key:'left',    label:'Align Left',   cmd:'justifyLeft'   },
    { key:'center',  label:'Center',       cmd:'justifyCenter' },
    { key:'right',   label:'Align Right',  cmd:'justifyRight'  },
    { key:'justify', label:'Justify',      cmd:'justifyFull'   },
  ].map(a => ({
    key: a.key,
    label: <span style={{ fontFamily:FONT, fontSize:13 }}>{a.label}</span>,
    onClick: () => { restoreSelection(); execOnCompose(a.cmd); },
  }));

  return (
    <RibbonGroup label="Paragraph">
      <div style={{ display:'flex', flexDirection:'column', gap:3 }}>

        {/* Row 1: Lists + Indent */}
        <div style={{ display:'flex', alignItems:'center', gap:2 }}>
          <FmtBtn title="Bullet List"    active={active.insertUnorderedList} onMouseDown={fmt('insertUnorderedList')}>
            <FontAwesomeIcon icon={faListUl} style={{ fontSize:12 }} />
          </FmtBtn>
          <FmtBtn title="Numbered List"  active={active.insertOrderedList}   onMouseDown={fmt('insertOrderedList')}>
            <FontAwesomeIcon icon={faListOl} style={{ fontSize:12 }} />
          </FmtBtn>
          <FmtBtn title="Decrease Indent" onMouseDown={fmt('outdent')}>
            <FontAwesomeIcon icon={faOutdent} style={{ fontSize:12 }} />
          </FmtBtn>
          <FmtBtn title="Increase Indent" onMouseDown={fmt('indent')}>
            <FontAwesomeIcon icon={faIndent} style={{ fontSize:12 }} />
          </FmtBtn>
        </div>

        {/* Row 2: Align dropdown + Undo/Redo */}
        <div style={{ display:'flex', alignItems:'center', gap:2 }}>
          <Dropdown menu={{ items: alignItems }} trigger={['click']} placement="bottomLeft">
            <div onMouseDown={saveSelection}>
              <FmtBtn title="Paragraph Alignment" active={false}>
                <span style={{ display:'flex', alignItems:'center', gap:2 }}>
                  {alignIcon}
                  <DownOutlined style={{ fontSize:7, color:C.textSec }} />
                </span>
              </FmtBtn>
            </div>
          </Dropdown>
          <FmtBtn title="Undo (Ctrl+Z)" onMouseDown={(e) => { e.preventDefault(); undoHistory(); }}>
            <span style={{ fontSize:13 }}>↩</span>
          </FmtBtn>
          <FmtBtn title="Redo (Ctrl+Y)" onMouseDown={(e) => { e.preventDefault(); redoHistory(); }}>
            <span style={{ fontSize:13 }}>↪</span>
          </FmtBtn>
        </div>
      </div>
    </RibbonGroup>
  );
}

// ---------------------------------------------------------------------------
// Format Text tab
// ---------------------------------------------------------------------------

function FormatTextTab() {
  const styleItems = [
    { key:'p',   label:'Normal',       onClick: () => execOnCompose('formatBlock','p')   },
    { key:'h1',  label:'Heading 1',    onClick: () => execOnCompose('formatBlock','h1')  },
    { key:'h2',  label:'Heading 2',    onClick: () => execOnCompose('formatBlock','h2')  },
    { key:'h3',  label:'Heading 3',    onClick: () => execOnCompose('formatBlock','h3')  },
    { key:'pre', label:'Preformatted', onClick: () => execOnCompose('formatBlock','pre') },
  ];

  return (
    <div style={s.tabContent}>

      {/* ── Basic Text ── */}
      <BasicTextGroup />
      <Sep />

      {/* ── Paragraph ── */}
      <ParagraphGroup />
      <Sep />

      {/* ── Styles ── */}
      <RibbonGroup label="Styles">
        <Dropdown menu={{ items: styleItems }} trigger={['click']} placement="bottomLeft">
          <div>
            <RibbonButton
              icon={<FontAwesomeIcon icon={faFont} style={{ fontSize:17, color:C.textSec }} />}
              label="Styles"
              title="Apply paragraph style"
            />
          </div>
        </Dropdown>
      </RibbonGroup>

    </div>
  );
}

// ---------------------------------------------------------------------------
// Options tab
// ---------------------------------------------------------------------------

function OptionsTab({ composeOptions = {}, onOptionsChange }) {
  // composeOptions shape:
  //   showBcc: boolean
  //   requestReadReceipt: boolean
  //   importance: 'normal' | 'high' | 'low'
  //   sensitivity: 'normal' | 'personal' | 'private' | 'confidential'

  const {
    showBcc           = false,
    requestReadReceipt = false,
    importance        = 'normal',
    sensitivity       = 'normal',
  } = composeOptions;

  function set(key, value) {
    if (typeof onOptionsChange === 'function') {
      onOptionsChange({ ...composeOptions, [key]: value });
    }
  }

  function toggle(key) {
    set(key, !composeOptions[key]);
  }

  // ── Importance dropdown items ─────────────────────────────────────────────
  const importanceItems = [
    {
      key: 'high',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {importance === 'high' ? <CheckOutlined style={{ color: C.blue }} /> : <span style={{ width: 14 }} />}
          <ExclamationCircleOutlined style={{ color: C.red }} />
          High
        </span>
      ),
      onClick: () => set('importance', 'high'),
    },
    {
      key: 'normal',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {importance === 'normal' ? <CheckOutlined style={{ color: C.blue }} /> : <span style={{ width: 14 }} />}
          <InfoCircleOutlined style={{ color: C.textSec }} />
          Normal
        </span>
      ),
      onClick: () => set('importance', 'normal'),
    },
    {
      key: 'low',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {importance === 'low' ? <CheckOutlined style={{ color: C.blue }} /> : <span style={{ width: 14 }} />}
          <MinusCircleOutlined style={{ color: C.textSec }} />
          Low
        </span>
      ),
      onClick: () => set('importance', 'low'),
    },
  ];

  // ── Sensitivity dropdown items ────────────────────────────────────────────
  const sensitivityItems = [
    'normal', 'personal', 'private', 'confidential',
  ].map(level => ({
    key: level,
    label: (
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {sensitivity === level
          ? <CheckOutlined style={{ color: C.blue }} />
          : <span style={{ width: 14 }} />}
        {level.charAt(0).toUpperCase() + level.slice(1)}
      </span>
    ),
    onClick: () => set('sensitivity', level),
  }));

  // ── Importance icon helper ────────────────────────────────────────────────
  function importanceIcon() {
    if (importance === 'high') return <ExclamationCircleOutlined style={{ fontSize: 19, color: C.red }} />;
    if (importance === 'low')  return <MinusCircleOutlined       style={{ fontSize: 19, color: C.textSec }} />;
    return                            <InfoCircleOutlined         style={{ fontSize: 19, color: C.textSec }} />;
  }

  // ── Sensitivity icon ──────────────────────────────────────────────────────
  const sensitivityColor = sensitivity === 'confidential' ? C.red
    : sensitivity === 'private' ? C.purple
    : C.textSec;

  return (
    <div style={s.tabContent}>

      {/* ── Show Fields ── */}
      <RibbonGroup label="Fields" column>
        <RibbonToggle
          icon={<MailOutlined style={{ fontSize: 13, color: C.blue }} />}
          label="Show Bcc"
          title="Show the Bcc field"
          checked={showBcc}
          onChange={() => toggle('showBcc')}
        />
      </RibbonGroup>
      <Sep />

      {/* ── Tracking ── */}
      <RibbonGroup label="Tracking" column>
        <RibbonToggle
          icon={<EyeOutlined style={{ fontSize: 13, color: C.textSec }} />}
          label="Request Read Receipt"
          title="Request a read receipt when message is opened"
          checked={requestReadReceipt}
          onChange={() => toggle('requestReadReceipt')}
        />
        <RibbonToggle
          icon={<CheckCircleOutlined style={{ fontSize: 13, color: C.textSec }} />}
          label="Request Delivery Receipt"
          title="Request a delivery receipt when message is delivered"
          checked={composeOptions.requestDeliveryReceipt || false}
          onChange={() => toggle('requestDeliveryReceipt')}
        />
      </RibbonGroup>
      <Sep />

      {/* ── Importance ── */}
      <RibbonGroup label="Tags">
        <Dropdown menu={{ items: importanceItems }} trigger={['click']} placement="bottomLeft">
          <div>
            <RibbonButton
              icon={importanceIcon()}
              label={
                <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  Importance <DownOutlined style={{ fontSize: 8 }} />
                </span>
              }
              title={`Importance: ${importance}`}
              active={importance !== 'normal'}
            />
          </div>
        </Dropdown>

        {/* ── Sensitivity ── */}
        <Dropdown menu={{ items: sensitivityItems }} trigger={['click']} placement="bottomLeft">
          <div>
            <RibbonButton
              icon={<LockOutlined style={{ fontSize: 19, color: sensitivityColor }} />}
              label={
                <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  Sensitivity <DownOutlined style={{ fontSize: 8 }} />
                </span>
              }
              title={`Sensitivity: ${sensitivity}`}
              active={sensitivity !== 'normal'}
            />
          </div>
        </Dropdown>
      </RibbonGroup>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ComposeRibbon — root component
// ---------------------------------------------------------------------------
//
// Props:
//   onSend          () => void
//   onSaveDraft     () => void
//   onDiscard       () => void
//   onAttach        () => void
//   composeOptions  { showBcc, requestReadReceipt, requestDeliveryReceipt,
//                     importance, sensitivity }
//   onOptionsChange (newOptions) => void
//   style           React.CSSProperties
// ---------------------------------------------------------------------------

export default function ComposeRibbon({
  onSend,
  onSaveDraft,
  onDiscard,
  onAttach,
  composeOptions = {},
  onOptionsChange,
  style,
}) {
  const dispatch  = useDispatch();
  const ribbon    = useSelector(selectRibbonState);

  // Compose ribbon uses compose.* tab IDs; default to message tab
  const rawTab    = ribbon.activeTabId || 'compose.message';
  const activeTab = rawTab.startsWith('compose.') ? rawTab : 'compose.message';

  const switchTab = useCallback((tabId) => {
    dispatch(setRibbonTab(tabId));
  }, [dispatch]);

  return (
    <>
      <style>{CSS}</style>

      <div style={{ ...s.root, ...style }}>

        {/* ── Tab bar ── */}
        <div style={s.tabBar}>
          <div style={s.tabList}>
            {TABS.map(tab => (
              <button
                key={tab.id}
                className={`cr-tab ${activeTab === tab.id ? 'cr-tab--active' : ''}`}
                onClick={() => switchTab(tab.id)}
                style={s.tab}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Tab panel ── */}
        <div style={s.panel}>
          {activeTab === 'compose.message' && (
            <MessageTab
              onSend={onSend}
              onSaveDraft={onSaveDraft}
              onDiscard={onDiscard}
              onAttach={onAttach}
            />
          )}
          {activeTab === 'compose.insert' && <InsertTab />}
          {activeTab === 'compose.formattext' && <FormatTextTab />}
          {activeTab === 'compose.options' && (
            <OptionsTab
              composeOptions={composeOptions}
              onOptionsChange={onOptionsChange}
            />
          )}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const s = {
  root: {
    display: 'flex', flexDirection: 'column',
    background: C.bg,
    borderBottom: `1px solid ${C.border}`,
    fontFamily: FONT,
    userSelect: 'none',
    flexShrink: 0,
  },

  tabBar: {
    display: 'flex', alignItems: 'center',
    borderBottom: `1px solid ${C.border}`,
    background: C.bgAlt,
    paddingLeft: 4,
    height: 32,
  },
  tabList: { display: 'flex', alignItems: 'flex-end', height: '100%' },
  tab: {
    height: '100%',
    padding: '0 14px',
    border: 'none',
    borderBottom: '2px solid transparent',
    background: 'transparent',
    color: C.textSec,
    fontSize: 12,
    fontWeight: 400,
    cursor: 'pointer',
    fontFamily: FONT,
    transition: 'color .12s, border-color .12s, background .12s',
    whiteSpace: 'nowrap',
  },

  panel: {
    overflowX: 'auto',
    overflowY: 'hidden',
    minHeight: 74,
  },
  tabContent: {
    display: 'flex', alignItems: 'stretch',
    padding: '4px 8px',
    gap: 0,
    minHeight: 66,
  },

  group: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'stretch',
    padding: '0 6px',
    minWidth: 0,
  },
  groupContent: {
    display: 'flex', alignItems: 'flex-start',
    gap: 2, flex: 1, flexWrap: 'wrap',
  },
  groupLabel: {
    textAlign: 'center', fontSize: 10, color: C.textMuted,
    fontFamily: FONT, paddingTop: 3,
    borderTop: `1px solid ${C.border}`,
    marginTop: 4,
    display: 'block',
  },

  sep: {
    width: 1, background: C.border,
    margin: '4px 4px', alignSelf: 'stretch',
    flexShrink: 0,
  },

  btn: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'flex-start',
    gap: 3,
    padding: '5px 7px',
    border: '1px solid transparent',
    borderRadius: 4,
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: FONT,
    minWidth: 44, maxWidth: 72,
    transition: 'background .1s, border-color .1s',
  },
  btnIcon:  { fontSize: 20, lineHeight: 1, display: 'flex', alignItems: 'center' },
  btnLabel: {
    fontSize: 11, color: C.textSec, textAlign: 'center',
    lineHeight: 1.2, whiteSpace: 'nowrap',
  },

  sbtn: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '4px 8px',
    border: '1px solid transparent',
    borderRadius: 3,
    background: 'transparent',
    cursor: 'pointer', fontFamily: FONT,
    whiteSpace: 'nowrap',
    transition: 'background .1s',
  },
  sbtnLabel: { fontSize: 12, color: C.textSec },
};

// ---------------------------------------------------------------------------
// CSS — hover / active states
// ---------------------------------------------------------------------------

const CSS = `
  /* Tab */
  .cr-tab:hover            { color:${C.text}; background:${C.bgHover}; }
  .cr-tab--active          { color:${C.blue}; font-weight:600;
                             border-bottom-color:${C.blue} !important;
                             background:${C.bg}; }
  .cr-tab--active:hover    { background:${C.bg}; }

  /* Large button */
  .cr-btn:hover            { background:${C.bgHover}; border-color:${C.border}; }
  .cr-btn--active          { background:${C.blueBg};  border-color:${C.blue}; }
  .cr-btn--active:hover    { background:${C.blueBg}; }
  .cr-btn--danger:hover    { background:${C.redBg};   border-color:${C.red}; }
  .cr-btn--danger:hover span { color:${C.red}; }
  .cr-btn--disabled        { opacity:.45; cursor:not-allowed !important; }

  /* Small button */
  .cr-sbtn:hover           { background:${C.bgHover}; border-color:${C.border}; }
  .cr-sbtn--active         { background:${C.blueBg};  border-color:${C.blue}; }
  .cr-sbtn--active:hover   { background:${C.blueBg}; }
  .cr-sbtn--danger:hover   { background:${C.redBg};   border-color:${C.red}; }
  .cr-sbtn--disabled       { opacity:.45; cursor:not-allowed !important; }

  /* Compact format buttons */
  .cr-fmtbtn:hover { background:${C.bgHover} !important; }
  .cr-fmtbtn:active { background:${C.border} !important; }

  /* Scrollbar */
  .cr-panel::-webkit-scrollbar       { height:4px; }
  .cr-panel::-webkit-scrollbar-thumb { background:${C.border}; border-radius:2px; }
`;
